import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

export interface BailTemplate {
  id: number;
  name: string;
  courrierId: number;
  content: string;
  OwnerId: number;
}

interface BailTemplateState {
  templates: BailTemplate[];
  defaultContent: string;
  loading: boolean;
  error: string | null;
}

const initialState: BailTemplateState = {
  templates: [],
  defaultContent: '',
  loading: false,
  error: null,
};

const headers = (token: string) => ({ Authorization: `bearer ${token}` });

export const fetchBailTemplates = createAsyncThunk<BailTemplate[], void, { state: RootState }>(
  'bailTemplate/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const OwnerId = authentification.user.OwnerId;
      const token   = authentification.user.token;
      const res = await axios.get(`${baseUrlApi}/bailtemplates/owner/${OwnerId}`, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur chargement modèles');
    }
  }
);

export const fetchDefaultContent = createAsyncThunk<string, void, { state: RootState }>(
  'bailTemplate/fetchDefault',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      const res   = await axios.get(`${baseUrlApi}/bailtemplates/default`, { headers: headers(token) });
      return res.data.content;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur');
    }
  }
);

export const createBailTemplate = createAsyncThunk<BailTemplate, { name: string; content: string }, { state: RootState }>(
  'bailTemplate/create',
  async (data, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      const res   = await axios.post(`${baseUrlApi}/bailtemplates`, data, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur création');
    }
  }
);

export const updateBailTemplate = createAsyncThunk<BailTemplate, { id: number; name: string; content: string }, { state: RootState }>(
  'bailTemplate/update',
  async ({ id, ...data }, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      const res   = await axios.put(`${baseUrlApi}/bailtemplates/${id}`, data, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur mise à jour');
    }
  }
);

export const deleteBailTemplate = createAsyncThunk<number, number, { state: RootState }>(
  'bailTemplate/delete',
  async (id, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      await axios.delete(`${baseUrlApi}/bailtemplates/${id}`, { headers: headers(token) });
      return id;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur suppression');
    }
  }
);

const bailTemplateSlice = createSlice({
  name: 'bailTemplate',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchBailTemplates.pending,  s => { s.loading = true; s.error = null; })
      .addCase(fetchBailTemplates.fulfilled, (s, a) => { s.loading = false; s.templates = a.payload; })
      .addCase(fetchBailTemplates.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; })

      .addCase(fetchDefaultContent.fulfilled, (s, a) => { s.defaultContent = a.payload; })

      .addCase(createBailTemplate.fulfilled, (s, a) => { s.templates.push(a.payload); })

      .addCase(updateBailTemplate.fulfilled, (s, a) => {
        const idx = s.templates.findIndex(t => t.id === a.payload.id);
        if (idx !== -1) s.templates[idx] = a.payload;
      })

      .addCase(deleteBailTemplate.fulfilled, (s, a) => {
        s.templates = s.templates.filter(t => t.id !== a.payload);
      });
  },
});

export default bailTemplateSlice.reducer;
